import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { getUserSettings, postUserSettings } from '@/api/core/userSetting';
import { log } from '@/utils/core/vueLogger.js';

// The period the user last applied on the direct-order screen, kept across sessions without having
// to save a View. The home widget reads it too: it shows that period when the user has no current
// objective.
//
// A group of its own rather than 'WEBAPP': WidgetLayout, KpiBuilder and SpsBuilder each post the
// whole 'WEBAPP' list from the copy they loaded at mount, so a key written there by this screen would
// be dropped by the next widget-layout save.
const GROUP_NAME = 'WEBAPP-OrderTracking';
const PERIOD_SETTING_NAME = 'OrderTrackingPeriod';

// What this session last saved. The save is not awaited by the screen, and the home widget reloads
// the moment the detail screen closes — reading the settings back then could still return the value
// from before the save.
let lastSaved = null;

function settingsFilter() {
	const userId = iframeDataBridge.getCurrentUser()?.id;
	return userId ? { groupName: GROUP_NAME, userId } : null;
}

/**
 * @returns {Promise<object|null>} what serializePeriodSelection() produced, or null
 */
export async function loadPeriodSelection() {
	const filter = settingsFilter();
	if (!filter) return null;
	if (lastSaved?.userId === filter.userId) return lastSaved.selection;

	try {
		const settings = await getUserSettings(filter);
		const setting = (Array.isArray(settings) ? settings : []).find((entry) => entry.name === PERIOD_SETTING_NAME);
		return setting?.value ? JSON.parse(setting.value) : null;
	} catch (error) {
		log('warn', 'OrderTracking', `⚠️ Saved period unreadable: ${error.message}`);
		return null;
	}
}

// Clearing writes `null` rather than posting an empty list, which the settings endpoint may treat as
// "nothing to update" and leave the old period in place.
export async function savePeriodSelection(selection) {
	const filter = settingsFilter();
	if (!filter) return false;

	lastSaved = { userId: filter.userId, selection: selection ?? null };
	const settings = [{ userId: filter.userId, groupName: GROUP_NAME, name: PERIOD_SETTING_NAME, value: JSON.stringify(selection ?? null) }];
	return postUserSettings(settings, filter);
}
